// ============================================================
// 卡牌系統 — runtime 牌組（有限循環，永不抽光）
// ============================================================
// StS 式模型：drawPile 抽完 → discardPile 洗回。
// 用引擎既有的 SeededRng 保證 PvP / replay 可重現。
//
// 起始平衡值已收斂到 balance.ts §14（路線圖 M1）：
//   CARD_OPENING_DRAW=5、CARD_DRAW_PER_TURN=3、CARD_HAND_CAP=8。
// 本檔 draw / tutor 的 handCap 預設值即取 CARD_HAND_CAP，呼叫端可覆寫。
// ============================================================

import { CARD_HAND_CAP } from '../balance.ts';
import { SeededRng } from '../server/rng.ts';
import type { CardId, CardInstance, DeckList } from './CardTypes.ts';

let _uidCounter = 0;
function mkUid(): string {
  return `c${(_uidCounter++).toString(36)}`;
}

export class Deck {
  drawPile: CardInstance[] = [];
  hand: CardInstance[] = [];
  discardPile: CardInstance[] = [];
  /** M6 desync fix — 本回合已排進 tick 但「尚未於 submit 消耗」的 cast 牌暫存區。
   *  刻意**不屬於 discardPile**：PvP 權威 deck（server）要到 submit 才 applyCasts，
   *  若 client 一放牌就丟進 discardPile，SCHED 中途濾牌觸發 reshuffle 時，client
   *  的 discardPile 會比 server 多出這些 cast 牌 → 洗出不同 drawPile → desync。
   *  改成暫存於此、submit 時 `commitHeld()` 一次併入 discardPile，與 server 對齊。 */
  heldPile: CardInstance[] = [];
  private rng: SeededRng;

  constructor(list: DeckList, seed: number) {
    this.rng = new SeededRng(seed);
    for (const [cardId, n] of Object.entries(list)) {
      for (let i = 0; i < n; i++) this.drawPile.push({ uid: mkUid(), cardId: cardId as CardId });
    }
    this.shuffle(this.drawPile);
  }

  /** 確定性 Fisher–Yates（吃 SeededRng）。 */
  private shuffle(a: CardInstance[]): void {
    for (let i = a.length - 1; i > 0; i--) {
      const j = this.rng.nextInt(0, i);
      [a[i], a[j]] = [a[j], a[i]];
    }
  }

  /** 抽 n 張到手牌。回傳實際抽到的卡。
   *  v2（方向 2）：抽牌堆抽完**不再洗回**（有限牌組）——空了就停，回收棄牌只能靠
   *  「跳 tick 抽牌」(`drawFromDiscard`)。原自動 `reshuffle` 已移除。 */
  draw(n: number, handCap = CARD_HAND_CAP): CardInstance[] {
    const drawn: CardInstance[] = [];
    for (let i = 0; i < n; i++) {
      if (this.drawPile.length === 0) break; // 抽牌堆空 → 不洗回，停止
      const card = this.drawPile.pop()!;
      // handCap 吃 hand + heldPile：SCHED 中途本回合已排 tick 的 cast 牌暫存 heldPile、
      // 尚未消耗，但仍算「玩家持有」。server 權威 deck 同時點這些牌還在 hand（submit 才
      // applyCasts），故 client 必須把 heldPile 計入才與 server 的滿手判斷一致（不 desync）。
      if (this.hand.length + this.heldPile.length >= handCap) {
        this.discardPile.push(card); // 滿手 → 直接進棄牌
        continue;
      }
      this.hand.push(card);
      drawn.push(card);
    }
    return drawn;
  }

  /** 棄牌堆洗回抽牌堆。
   *  ⚠️ v2（方向 2）起 `draw` **不再自動呼叫本方法**（抽牌堆有限、抽完就停）。
   *  方法保留供測試 / 未來需要；canonical 排序邏輯不變。
   *  M6 — 洗牌前先依 cardId canonical 排序：PvP 雙端（client 互動式出牌 vs server
   *  權威 deck）的 discardPile 寫入順序可能不同，但牌的「多重集合」一致；先排序再
   *  用同 seed 洗牌，即保證兩端洗出 byte-identical 的 drawPile（cardId 序列），不會
   *  desync。同 cardId 的牌彼此可替換，uid 順序差異不影響 cardId 序列。 */
  reshuffle(): void {
    this.drawPile.push(...this.discardPile);
    this.discardPile.length = 0;
    this.drawPile.sort((a, b) => (a.cardId < b.cardId ? -1 : a.cardId > b.cardId ? 1 : 0));
    this.shuffle(this.drawPile);
  }

  /** 出牌 / 棄牌：從手牌移到棄牌堆。 */
  discardFromHand(uid: string): void {
    const idx = this.hand.findIndex((c) => c.uid === uid);
    if (idx >= 0) this.discardPile.push(...this.hand.splice(idx, 1));
  }

  /** 棄掉手牌中第一張指定 cardId（M6 server 權威 deck 用 cardId 同步，uid 跨端不通用）。
   *  成功回 true、手上無此 cardId 回 false。 */
  discardOneByCardId(cardId: CardId): boolean {
    const idx = this.hand.findIndex((c) => c.cardId === cardId);
    if (idx < 0) return false;
    this.discardPile.push(...this.hand.splice(idx, 1));
    return true;
  }

  /** 出牌放進 tick：把手牌移到 heldPile（**不進 discardPile**，見 heldPile 說明）。
   *  M6 desync fix：取代原本在放牌當下 discardFromHand 的作法。 */
  holdFromHand(uid: string): void {
    const idx = this.hand.findIndex((c) => c.uid === uid);
    if (idx >= 0) this.heldPile.push(...this.hand.splice(idx, 1));
  }

  /** 取消 tick 出牌：把 heldPile 指定 uid 的牌移回手牌（M4b 清除 tick 用）。
   *  找不到回 false；還牌一律允許（即使暫時超過手牌上限）。 */
  returnHeldToHand(uid: string): boolean {
    const idx = this.heldPile.findIndex((c) => c.uid === uid);
    if (idx < 0) return false;
    this.hand.push(...this.heldPile.splice(idx, 1));
    return true;
  }

  /** submit 時把本回合所有 tick 出牌（heldPile）一次併入 discardPile，
   *  對齊 server `applyCasts` 的消耗時機（雙端 discardPile 多重集合一致）。 */
  commitHeld(): void {
    this.discardPile.push(...this.heldPile);
    this.heldPile.length = 0;
  }

  /** 跳 tick 抽牌：從棄牌堆**隨機**抽 1 張到手牌（空棄牌 / 滿手回 null）。
   *  用 Math.random（非 SeededRng）——此機制目前僅單機使用，不參與 PvP 確定性同步，
   *  刻意不擾動 deck 的洗牌隨機流。滿手判斷同 draw：吃 hand + heldPile。 */
  drawFromDiscard(handCap = CARD_HAND_CAP): CardInstance | null {
    if (this.discardPile.length === 0) return null;
    if (this.hand.length + this.heldPile.length >= handCap) return null;
    const idx = Math.floor(Math.random() * this.discardPile.length);
    const [card] = this.discardPile.splice(idx, 1);
    this.hand.push(card);
    return card;
  }

  /** 搜牌：把牌庫中第一張指定 cardId 移到手牌（找不到 / 滿手回 null）。 */
  tutor(cardId: CardId, handCap = CARD_HAND_CAP): CardInstance | null {
    const idx = this.drawPile.findIndex((c) => c.cardId === cardId);
    // handCap 吃 hand + heldPile（同 draw：與 server 權威 deck 的滿手判斷對齊）。
    if (idx < 0 || this.hand.length + this.heldPile.length >= handCap) return null;
    const [card] = this.drawPile.splice(idx, 1);
    this.hand.push(card);
    return card;
  }

  get counts(): { draw: number; hand: number; discard: number } {
    return { draw: this.drawPile.length, hand: this.hand.length, discard: this.discardPile.length };
  }
}
